import { ArrowType } from "@/gen_src/proto/chalk/arrow/v1/arrow_pb";
import { compileOnlyAssertNever } from "@/utils/asserts";
import { ArrowTypeWrapper } from "@/utils/graph/GraphInterface";
import { range } from "lodash";
import { arbitraryStringFromDecimalValue } from "@/utils/string-utils";

const decimalIntegerPattern = /^-?\d+$/;

export const formatArrowDecimalValue = (value: unknown, scale: number): string | null => {
  const rawValue = arbitraryStringFromDecimalValue(value)?.trim();
  if (rawValue == null || !decimalIntegerPattern.test(rawValue)) {
    return rawValue ?? null;
  }

  const sign = rawValue.startsWith("-") ? "-" : "";
  const digits = sign ? rawValue.slice(1) : rawValue;

  if (scale === 0) {
    return `${sign}${digits}`;
  }

  if (scale < 0) {
    return `${sign}${digits}${"0".repeat(Math.abs(scale))}`;
  }

  const padded = digits.padStart(scale + 1, "0");
  const whole = padded.slice(0, -scale);
  const fractional = padded.slice(-scale);
  return `${sign}${whole}.${fractional}`;
};

export function recursiveStringifyArrowType(typeWrapper: ArrowTypeWrapper, maxDepth: number = 3) {
  const rawString = stringifyArrowTypeHelper(typeWrapper.arrowType, maxDepth, 0);

  return typeWrapper.isNullable ? `Opt[${rawString}]` : rawString;
}

function stringifyArrowTypeHelper(arrowType: ArrowType, maxDepth: number, depth: number): string {
  if (depth >= maxDepth) {
    return "...";
  }
  switch (arrowType.arrowTypeEnum.case) {
    case "none":
    case "bool":
    case "float64":
    case "int64":
    case "largeUtf8":
    case "timestamp":
    case "date64":
    case "time64":
    case "duration":
    case "utf8":
    case "int8":
    case "int16":
    case "int32":
    case "uint8":
    case "uint16":
    case "uint32":
    case "uint64":
    case "float16":
    case "float32":
    case "date32":
    case "time32":
    case "binary":
    case "largeBinary":
    case "fixedSizeBinary":
    case "decimal128":
    case "decimal256":
      return arrowType.arrowTypeEnum.case;
    case "extension": {
      return arrowType.arrowTypeEnum.case;
    }
    case "map": {
      let output = "map{\n";
      output += range(0, depth + 1)
        .map(() => " ")
        .join("");
      if (arrowType.arrowTypeEnum.value.keyField?.arrowType != null) {
        output += "keys: ";
        const nested = stringifyArrowTypeHelper(
          arrowType.arrowTypeEnum.value.keyField.arrowType,
          maxDepth,
          depth + 1,
        );
        output += arrowType.arrowTypeEnum.value.keyField.nullable ? `Opt[${nested}]` : nested;
        output += ",\n";
      } else {
        output += "keys: unknown,\n";
      }
      output += range(0, depth + 1)
        .map(() => " ")
        .join("");
      if (arrowType.arrowTypeEnum.value.itemField?.arrowType != null) {
        output += "values: ";
        const nested = stringifyArrowTypeHelper(
          arrowType.arrowTypeEnum.value.itemField.arrowType,
          maxDepth,
          depth + 1,
        );
        output += arrowType.arrowTypeEnum.value.itemField.nullable ? `Opt[${nested}]` : nested;
        output += "\n";
      } else {
        output += "values: unknown,\n";
      }
      output += range(0, depth)
        .map(() => " ")
        .join("");
      output += "}";
      return output;
    }
    case "struct": {
      let output = "struct{\n";
      const len = arrowType.arrowTypeEnum.value.subFieldTypes.length;
      arrowType.arrowTypeEnum.value.subFieldTypes.forEach((field, i) => {
        if (field.arrowType != null) {
          output += range(0, depth + 1)
            .map(() => " ")
            .join("");
          output += field.name + ": ";
          const nested = stringifyArrowTypeHelper(field.arrowType, maxDepth, depth + 1);
          output += field.nullable ? `Opt[${nested}]` : nested;
          if (i !== len - 1) {
            output += ",\n";
          }
        }
      });
      output += "\n";
      output += range(0, depth)
        .map(() => " ")
        .join("");
      output += "}";
      return output;
    }
    case "largeList":
    case "list":
    case "fixedSizeList": {
      let output = `${arrowType.arrowTypeEnum.case}[`;
      if (arrowType.arrowTypeEnum.value.fieldType?.arrowType != null) {
        const nested = stringifyArrowTypeHelper(
          arrowType.arrowTypeEnum.value.fieldType.arrowType,
          maxDepth,
          depth + 1,
        );
        output += arrowType.arrowTypeEnum.value.fieldType?.nullable ? `Opt[${nested}]` : nested;
      } else {
        output += "unknown";
      }
      output += "]";
      return output;
    }
    case undefined:
      return "unknown";
    default:
      compileOnlyAssertNever(arrowType.arrowTypeEnum);
      return "unknown";
  }
}

export function simpleRenderArrowType(typeWrapper: ArrowTypeWrapper): string {
  const baseType = renderBaseArrowType(typeWrapper.arrowType);
  return typeWrapper.isNullable ? `Opt[${baseType}]` : baseType;
}

function renderBaseArrowType(arrowType: ArrowType): string {
  switch (arrowType.arrowTypeEnum.case) {
    case undefined:
      return "unknown";
    case "none":
      return "none";
    case "bool":
      return "bool";
    case "struct":
      return "struct";
    case "int64":
    case "int8":
    case "int16":
    case "int32":
    case "uint8":
    case "uint16":
    case "uint32":
    case "uint64":
      return "int";
    case "float64":
    case "float16":
    case "float32":
      return "float";
    case "largeUtf8":
    case "utf8":
      return "str";
    case "timestamp":
      return "timestamp";
    case "date64":
    case "date32":
      return "date";
    case "largeList":
    case "list":
    case "fixedSizeList":
      return "list";
    case "time64":
    case "time32":
      return "time";
    case "duration":
      return "duration";
    case "binary":
    case "largeBinary":
    case "fixedSizeBinary":
      return "binary";
    case "decimal128":
    case "decimal256":
      return "decimal";
    case "map":
      return "map";
    case "extension":
      return "extension";
    default:
      compileOnlyAssertNever(arrowType.arrowTypeEnum);
      return "unknown";
  }
}
